import type {
  AbastecimentoDto,
  ContratoDto,
  LocacaoDto,
  LocacoesSnapshot,
  ManutencaoDto,
  MaquinarioDto,
  OperationalMetricsDto,
  PeriodRange,
  RegistroHorimetroDto,
} from "@/services/locacoes";
import {
  isContratoPorCarradas,
  isContratoPorHoras,
  isContratoEquipamento,
  isContratoModeloMensalEquipamento,
  resolveAtivoTipo,
  type AtivoTipo,
  type FiltroTipoAtivo,
} from "@/services/locacoes";
import { currentDatePeriod } from "@/utils/dateInput";
import { formatNumber } from "@/utils/format";

export function defaultLocacoesPeriod(): PeriodRange {
  return currentDatePeriod();
}

function isoDateLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, (m ?? 1) - 1, d ?? 1);
  date.setDate(date.getDate() + days);
  return isoDateLocal(date);
}

/** Desloca o intervalo pela própria duração. direction -1 = período anterior. */
export function shiftPeriodByLength(period: PeriodRange, direction: -1 | 1): PeriodRange {
  const start = new Date(`${period.startDate}T12:00:00`);
  const end = new Date(`${period.endDate}T12:00:00`);
  const length = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
  const delta = length * direction;
  return {
    startDate: addDaysIso(period.startDate, delta),
    endDate: addDaysIso(period.endDate, delta),
  };
}

/** Semana de segunda a domingo. offset 0 = semana corrente; negativo volta semanas. */
export function weekPeriod(offsetWeeks = 0): PeriodRange {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const weekday = start.getDay();
  const diffToMonday = weekday === 0 ? -6 : 1 - weekday;
  start.setDate(start.getDate() + diffToMonday + offsetWeeks * 7);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { startDate: isoDateLocal(start), endDate: isoDateLocal(end) };
}

export function periodForContrato(
  contrato: Pick<ContratoDto, "dataInicio" | "dataFimPrevista">,
  registeredDates: Array<string | null | undefined> = [],
): PeriodRange {
  const today = currentDatePeriod().startDate;
  const dates = [
    contrato.dataInicio,
    contrato.dataFimPrevista,
    today,
    ...registeredDates,
  ]
    .map((date) => date?.slice(0, 10))
    .filter((date): date is string => Boolean(date && /^\d{4}-\d{2}-\d{2}$/.test(date)));

  return {
    startDate: dates.reduce((earliest, date) => date < earliest ? date : earliest, today),
    endDate: dates.reduce((latest, date) => date > latest ? date : latest, today),
  };
}

export function buildReportPeriodForContrato(
  contrato: Pick<ContratoDto, "dataInicio" | "dataFimPrevista">,
): PeriodRange {
  return periodForContrato(contrato);
}

export function formatHoras(horas: number): string {
  const h = Math.floor(horas);
  const m = Math.round((horas - h) * 60);
  if (h === 0 && m === 0) return "0h";
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function formatPeriodLabel(period: PeriodRange): string {
  const fmt = (d: string) => {
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  };
  return `${fmt(period.startDate)} – ${fmt(period.endDate)}`;
}

export function filterRegistrosByPeriod(regs: RegistroHorimetroDto[], period: PeriodRange): RegistroHorimetroDto[] {
  return regs.filter((r) => {
    const d = r.dataHora.slice(0, 10);
    return d >= period.startDate && d <= period.endDate;
  });
}

export function filterRegistrosDoContrato(
  contrato: Pick<ContratoDto, "id" | "maquinaIds" | "dataInicio">,
  registros: RegistroHorimetroDto[],
): RegistroHorimetroDto[] {
  const scoped = registros.filter((r) => contrato.maquinaIds.includes(r.maquinaId));
  const byContrato = scoped.filter((r) => r.contratoId === contrato.id);
  if (byContrato.length === 0) {
    return [];
  }
  const byId = new Map(registros.map((r) => [r.id, r]));
  const included = new Map(byContrato.map((r) => [r.id, r]));
  for (const r of byContrato) {
    if (!r.registroInicioId) continue;
    const linked = byId.get(r.registroInicioId);
    if (linked && contrato.maquinaIds.includes(linked.maquinaId) && !included.has(linked.id)) {
      included.set(linked.id, linked);
    }
  }
  return [...included.values()];
}

export function getHorimetroContratoMaquina(
  contrato: Pick<ContratoDto, "id">,
  maquinaId: string,
  registros: RegistroHorimetroDto[],
): number | null {
  const scoped = registros.filter((r) => r.contratoId === contrato.id && r.maquinaId === maquinaId);
  if (scoped.length === 0) return null;
  return Math.max(...scoped.map((r) => r.horimetroInformado));
}

/** Pré-seleciona máquina quando há preset (Garagem) ou apenas uma opção no escopo. */
export function resolveDefaultMaquinaId(
  maquinarios: MaquinarioDto[],
  presetMaquinaId?: string | null,
): string {
  if (presetMaquinaId && maquinarios.some((m) => m.id === presetMaquinaId)) {
    return presetMaquinaId;
  }
  const ativas = maquinarios.filter((m) => m.status !== "inativo");
  if (ativas.length === 1) return ativas[0].id;
  if (maquinarios.length === 1) return maquinarios[0].id;
  return "";
}

/** Horímetro sugerido: prioriza último registro do contrato, senão horímetro global da máquina. */
export function resolveHorimetroSugestao(
  maquina: MaquinarioDto | undefined,
  contratoContext?: { contrato: Pick<ContratoDto, "id">; maquinaId: string; registros: RegistroHorimetroDto[] } | null,
): number {
  if (!maquina) return 0;
  if (contratoContext && contratoContext.maquinaId === maquina.id) {
    const contratoH = getHorimetroContratoMaquina(
      contratoContext.contrato,
      contratoContext.maquinaId,
      contratoContext.registros,
    );
    if (contratoH != null) return contratoH;
  }
  return maquina.horimetroAtual;
}

export interface HorimetroDiaRow {
  id: string;
  maquinaId: string;
  maquinaNome: string;
  /** Dia civil de referência (dia do final, ou do início se aberto). */
  data: string;
  inicio?: RegistroHorimetroDto;
  final?: RegistroHorimetroDto;
  horasTrabalhadas: number | null;
  operacaoAberta: boolean;
}

/** Agrupa pares início→final; horas atribuídas ao dia civil do final. */
export function groupRegistrosPorDia(regs: RegistroHorimetroDto[]): HorimetroDiaRow[] {
  const byId = new Map(regs.map((r) => [r.id, r]));
  const sorted = [...regs]
    .filter((r) => r.statusAprovacao !== "rejeitado")
    .sort((a, b) => a.dataHora.localeCompare(b.dataHora) || a.createdAt.localeCompare(b.createdAt));

  const rows: HorimetroDiaRow[] = [];
  const linkedFinalIds = new Set<string>();

  for (const r of sorted) {
    if (r.tipo !== "final") continue;
    const inicio =
      r.registroInicioId != null
        ? byId.get(r.registroInicioId)
        : [...sorted]
            .filter((x) => x.tipo === "inicio" && x.maquinaId === r.maquinaId && x.dataHora <= r.dataHora)
            .sort((a, b) => b.dataHora.localeCompare(a.dataHora))[0];

    linkedFinalIds.add(r.id);
    const horas =
      r.horasTrabalhadas != null
        ? r.horasTrabalhadas
        : inicio
          ? Math.max(0, r.horimetroInformado - inicio.horimetroInformado)
          : null;

    rows.push({
      id: r.id,
      maquinaId: r.maquinaId,
      maquinaNome: r.maquinaNome,
      data: r.dataHora.slice(0, 10),
      inicio,
      final: r,
      horasTrabalhadas: horas,
      operacaoAberta: false,
    });
  }

  for (const r of sorted) {
    if (r.tipo !== "inicio") continue;
    const hasFinal = rows.some((row) => row.inicio?.id === r.id);
    if (hasFinal) continue;
    rows.push({
      id: r.id,
      maquinaId: r.maquinaId,
      maquinaNome: r.maquinaNome,
      data: r.dataHora.slice(0, 10),
      inicio: r,
      horasTrabalhadas: null,
      operacaoAberta: true,
    });
  }

  return rows.sort((a, b) => b.data.localeCompare(a.data) || b.id.localeCompare(a.id));
}

/** Linha entra no período se início ou final estiver no intervalo. Sessões abertas sempre aparecem. */
export function filterHorimetroDiaRowsByPeriod(rows: HorimetroDiaRow[], period: PeriodRange): HorimetroDiaRow[] {
  return rows.filter((row) => {
    if (row.operacaoAberta) return true;
    const dates = [row.data, row.inicio?.dataHora.slice(0, 10), row.final?.dataHora.slice(0, 10)].filter(Boolean);
    return dates.some((d) => d! >= period.startDate && d! <= period.endDate);
  });
}

/** Registros fora do período que não aparecem na tabela (sessões abertas sempre visíveis). */
export function countRegistrosHorimetroForaDoPeriodo(
  registros: RegistroHorimetroDto[],
  period: PeriodRange,
  maquinaIds?: Set<string>,
): number {
  const scoped = registros.filter((r) => {
    if (r.statusAprovacao === "rejeitado") return false;
    if (maquinaIds && !maquinaIds.has(r.maquinaId)) return false;
    return true;
  });
  const rows = groupRegistrosPorDia(scoped);
  const visibleIds = new Set<string>();
  for (const row of filterHorimetroDiaRowsByPeriod(rows, period)) {
    if (row.inicio) visibleIds.add(row.inicio.id);
    if (row.final) visibleIds.add(row.final.id);
  }
  return scoped.filter((r) => {
    const d = r.dataHora.slice(0, 10);
    const outside = d < period.startDate || d > period.endDate;
    return outside && !visibleIds.has(r.id);
  }).length;
}

export function listMaquinaIdsEmContratoAtivo(
  contratos: ContratoDto[],
  excludeContratoId?: string,
): Set<string> {
  const set = new Set<string>();
  for (const c of contratos) {
    if (c.status !== "ativo" || c.id === excludeContratoId) continue;
    for (const mid of c.maquinaIds) set.add(mid);
  }
  return set;
}

/** Contagem de registros por dia no período (para gráfico quando ainda não há pares início/final). */
export function computeRegistrosPorDia(
  registros: RegistroHorimetroDto[],
  period: PeriodRange,
): Array<{ date: string; count: number }> {
  const filtered = filterRegistrosByPeriod(registros, period);
  const porDia = new Map<string, number>();
  for (const r of filtered) {
    const day = r.dataHora.slice(0, 10);
    porDia.set(day, (porDia.get(day) ?? 0) + 1);
  }
  const rows: Array<{ date: string; count: number }> = [];
  const cursor = new Date(`${period.startDate}T12:00:00`);
  const end = new Date(`${period.endDate}T12:00:00`);
  while (cursor <= end) {
    const d = cursor.toISOString().slice(0, 10);
    rows.push({ date: d, count: porDia.get(d) ?? 0 });
    cursor.setDate(cursor.getDate() + 1);
  }
  return rows;
}

export function filterRegistrosHorasChart(
  registros: RegistroHorimetroDto[],
  opts: {
    maquinaId?: string;
    contrato?: Pick<ContratoDto, "id" | "maquinaIds" | "dataInicio"> | null;
  },
): RegistroHorimetroDto[] {
  let list = registros;
  if (opts.contrato) {
    list = filterRegistrosDoContrato(opts.contrato, list);
  }
  if (opts.maquinaId) {
    list = list.filter((r) => r.maquinaId === opts.maquinaId);
  }
  return list;
}

/** Horas por dia civil, com filtros opcionais de máquina e contrato. */
export function computeHorasPorDiaFiltrado(
  registros: RegistroHorimetroDto[],
  period: PeriodRange,
  opts?: {
    maquinaId?: string;
    contrato?: Pick<ContratoDto, "id" | "maquinaIds" | "dataInicio"> | null;
  },
): Array<{ date: string; horas: number }> {
  const scoped = filterRegistrosHorasChart(registros, opts ?? {});
  const rows = filterHorimetroDiaRowsByPeriod(groupRegistrosPorDia(scoped), period);
  const porDia = new Map<string, number>();
  for (const row of rows) {
    if (row.horasTrabalhadas == null || row.horasTrabalhadas <= 0) continue;
    const day = row.data;
    if (day >= period.startDate && day <= period.endDate) {
      porDia.set(day, (porDia.get(day) ?? 0) + row.horasTrabalhadas);
    }
  }
  const result: Array<{ date: string; horas: number }> = [];
  const cursor = new Date(`${period.startDate}T12:00:00`);
  const end = new Date(`${period.endDate}T12:00:00`);
  while (cursor <= end) {
    const d = cursor.toISOString().slice(0, 10);
    result.push({ date: d, horas: Math.round((porDia.get(d) ?? 0) * 100) / 100 });
    cursor.setDate(cursor.getDate() + 1);
  }
  return result;
}

export function computeRegistrosPorDiaFiltrado(
  registros: RegistroHorimetroDto[],
  period: PeriodRange,
  opts?: {
    maquinaId?: string;
    contrato?: Pick<ContratoDto, "id" | "maquinaIds" | "dataInicio"> | null;
  },
): Array<{ date: string; count: number }> {
  const scoped = filterRegistrosHorasChart(registros, opts ?? {});
  return computeRegistrosPorDia(scoped, period);
}

/** Máquinas com registro de início no período sem final correspondente. */
export function countSessoesAbertas(registros: RegistroHorimetroDto[], period: PeriodRange): number {
  const filtered = filterRegistrosByPeriod(registros, period);
  const openByMaquina = new Map<string, boolean>();
  const sorted = [...filtered].sort((a, b) => a.dataHora.localeCompare(b.dataHora));
  for (const reg of sorted) {
    if (reg.tipo === "inicio") openByMaquina.set(reg.maquinaId, true);
    else if (reg.tipo === "final" || reg.tipo === "parada") openByMaquina.set(reg.maquinaId, false);
  }
  return [...openByMaquina.values()].filter(Boolean).length;
}

export function filterAbastecimentosByPeriod(abs: AbastecimentoDto[], period: PeriodRange): AbastecimentoDto[] {
  return abs.filter((a) => a.data >= period.startDate && a.data <= period.endDate);
}

/** Abastecimentos vinculados a contratos ativos (exclui órfãos de garagem sem contrato). */
export function filterAbastecimentosContratosAtivos(
  contratos: ContratoDto[],
  abastecimentos: AbastecimentoDto[],
): AbastecimentoDto[] {
  const seen = new Map<string, AbastecimentoDto>();
  for (const c of contratos) {
    if (c.status === "cancelado") continue;
    for (const a of filterAbastecimentosDoContrato(c, abastecimentos)) {
      seen.set(a.id, a);
    }
  }
  return [...seen.values()];
}

function previousPeriodRange(startDate: string, endDate: string): PeriodRange {
  const start = new Date(`${startDate}T12:00:00`);
  const end = new Date(`${endDate}T12:00:00`);
  const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
  const prevEnd = new Date(start);
  prevEnd.setDate(prevEnd.getDate() - 1);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - (days - 1));
  return {
    startDate: prevStart.toISOString().slice(0, 10),
    endDate: prevEnd.toISOString().slice(0, 10),
  };
}

export function pctDelta(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 1000) / 10;
}

export function computeAbastecimentosResumoPeriodo(
  abastecimentos: AbastecimentoDto[],
  period: PeriodRange,
): { count: number; litros: number; consumoMedio: number; deltaCount: number; deltaLitros: number } {
  const noPeriodo = filterAbastecimentosByPeriod(abastecimentos, period);
  const litros = noPeriodo.reduce((s, a) => s + a.litros, 0);
  const prev = previousPeriodRange(period.startDate, period.endDate);
  const prevList = filterAbastecimentosByPeriod(abastecimentos, prev);
  const prevLitros = prevList.reduce((s, a) => s + a.litros, 0);
  const withLph = noPeriodo.filter((a) => a.litrosPorHora != null && a.litrosPorHora > 0);
  const consumoMedio =
    withLph.length > 0
      ? Math.round((withLph.reduce((s, a) => s + (a.litrosPorHora ?? 0), 0) / withLph.length) * 10) / 10
      : 0;
  return {
    count: noPeriodo.length,
    litros: Math.round(litros * 10) / 10,
    consumoMedio,
    deltaCount: pctDelta(noPeriodo.length, prevList.length),
    deltaLitros: pctDelta(litros, prevLitros),
  };
}

function contratoPeriodEnd(
  contrato: Pick<ContratoDto, "dataFimPrevista" | "updatedAt">,
  fallbackDate?: string,
): string {
  return contrato.dataFimPrevista ?? fallbackDate?.slice(0, 10) ?? contrato.updatedAt.slice(0, 10);
}

/** Abastecimentos vinculados ao contrato (contratoId ou legado no período da máquina). */
export function filterAbastecimentosDoContrato(
  contrato: Pick<ContratoDto, "id" | "maquinaIds" | "dataInicio" | "dataFimPrevista" | "updatedAt">,
  abastecimentos: AbastecimentoDto[],
  fallbackEnd?: string,
): AbastecimentoDto[] {
  const id = contrato.id;
  const fim = contratoPeriodEnd(contrato, fallbackEnd);
  const inPeriodOnMachine = (a: AbastecimentoDto) =>
    contrato.maquinaIds.includes(a.maquinaId) && a.data >= contrato.dataInicio && a.data <= fim;

  const byContrato = abastecimentos.filter((a) => a.contratoId === id);
  const legacyNoContrato = abastecimentos.filter((a) => !a.contratoId && inPeriodOnMachine(a));

  if (byContrato.length > 0) {
    const seen = new Set(byContrato.map((a) => a.id));
    return [...byContrato, ...legacyNoContrato.filter((a) => !seen.has(a.id))];
  }
  return legacyNoContrato;
}

export function filterManutencoesDoContrato(
  contrato: Pick<ContratoDto, "maquinaIds" | "dataInicio" | "dataFimPrevista" | "updatedAt">,
  manutencoes: import("@/services/locacoes").ManutencaoDto[],
  fallbackEnd?: string,
): import("@/services/locacoes").ManutencaoDto[] {
  const fim = contratoPeriodEnd(contrato, fallbackEnd);
  return manutencoes.filter(
    (m) =>
      contrato.maquinaIds.includes(m.maquinaId) &&
      m.dataAbertura >= contrato.dataInicio &&
      m.dataAbertura <= fim,
  );
}

export function computeMaquinaHorasNoPeriodo(
  maquinaId: string,
  registros: RegistroHorimetroDto[],
  period: PeriodRange,
): number {
  const rows = filterHorimetroDiaRowsByPeriod(
    groupRegistrosPorDia(registros.filter((r) => r.maquinaId === maquinaId)),
    period,
  );
  return rows.reduce((s, row) => s + (row.horasTrabalhadas ?? 0), 0);
}

/** Exclui horímetros de contratos em que o cliente paga combustível (não entram no consumo L/h). */
export function filterRegistrosParaConsumoContrato(
  contratos: ContratoDto[],
  registros: RegistroHorimetroDto[],
): RegistroHorimetroDto[] {
  const excludeIds = new Set(contratos.filter((c) => c.combustivelPagoCliente).map((c) => c.id));
  if (excludeIds.size === 0) return registros;
  return registros.filter((r) => !r.contratoId || !excludeIds.has(r.contratoId));
}

function abastecimentoSequenceKey(a: Pick<AbastecimentoDto, "maquinaId" | "contratoId">): string {
  return `${a.maquinaId}::${a.contratoId ?? ""}`;
}

/** L/h exibido: valor salvo, intervalo entre abastecimentos ou litros ÷ horas do horímetro (como no dashboard). */
export function resolveLitrosPorHoraAbastecimento(
  abastecimento: AbastecimentoDto,
  abastecimentosEscopo: AbastecimentoDto[],
  opts?: {
    registros?: RegistroHorimetroDto[];
    period?: PeriodRange;
  },
): number | null {
  if (abastecimento.litrosPorHora != null && abastecimento.litrosPorHora > 0) {
    return abastecimento.litrosPorHora;
  }

  const sequence = abastecimentosEscopo
    .filter((a) => abastecimentoSequenceKey(a) === abastecimentoSequenceKey(abastecimento))
    .sort((a, b) => a.data.localeCompare(b.data) || a.createdAt.localeCompare(b.createdAt));

  const idx = sequence.findIndex((a) => a.id === abastecimento.id);
  if (idx > 0) {
    const prev = sequence[idx - 1];
    const horas = Math.max(0, abastecimento.horimetroAtual - prev.horimetroAtual);
    if (horas > 0) return Math.round((abastecimento.litros / horas) * 100) / 100;
  }

  if (opts?.registros && opts?.period) {
    const horas = computeMaquinaHorasNoPeriodo(abastecimento.maquinaId, opts.registros, opts.period);
    if (horas > 0) return Math.round((abastecimento.litros / horas) * 10) / 10;
  }

  return null;
}

/** Consumo médio no período: litros abastecidos ÷ horas trabalhadas (mesma lógica do dashboard). */
export function computeConsumoMedioAbastecimentosPeriodo(
  abastecimentos: AbastecimentoDto[],
  registros: RegistroHorimetroDto[],
  period: PeriodRange,
  maquinaIds: string[],
): number {
  const filtered = filterAbastecimentosByPeriod(abastecimentos, period).filter((a) =>
    maquinaIds.includes(a.maquinaId),
  );
  const litros = filtered.reduce((s, a) => s + a.litros, 0);
  const horas = maquinaIds.reduce((s, id) => s + computeMaquinaHorasNoPeriodo(id, registros, period), 0);
  if (horas > 0) return Math.round((litros / horas) * 10) / 10;

  const lphList = filtered
    .map((a) => resolveLitrosPorHoraAbastecimento(a, abastecimentos, { registros, period }))
    .filter((v): v is number => v != null && v > 0);
  return lphList.length > 0
    ? Math.round((lphList.reduce((s, v) => s + v, 0) / lphList.length) * 10) / 10
    : 0;
}

export function buildMaquinasOperacao(
  maquinarios: MaquinarioDto[],
  registros: RegistroHorimetroDto[],
  period: PeriodRange,
): Array<{
  id: string;
  nome: string;
  modelo: string;
  status: MaquinarioDto["status"];
  horimetroAtual: number;
  horasPeriodo: number;
  registrosPeriodo: number;
  operacaoAberta: boolean;
}> {
  const filtered = filterRegistrosByPeriod(registros, period);
  return maquinarios.map((m) => {
    const maquinaRegs = filtered
      .filter((r) => r.maquinaId === m.id)
      .sort((a, b) => a.dataHora.localeCompare(b.dataHora));
    let operacaoAberta = false;
    for (const reg of maquinaRegs) {
      if (reg.tipo === "inicio") operacaoAberta = true;
      else if (reg.tipo === "final" || reg.tipo === "parada") operacaoAberta = false;
    }
    return {
      id: m.id,
      nome: m.nome,
      modelo: m.modelo || m.tipo,
      status: m.status,
      horimetroAtual: m.horimetroAtual,
      horasPeriodo: computeMaquinaHorasNoPeriodo(m.id, registros, period),
      registrosPeriodo: maquinaRegs.length,
      operacaoAberta,
    };
  });
}

export function downloadCsv(filename: string, headers: string[], rows: string[][]): void {
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const lines = [headers.map(escape).join(";"), ...rows.map((r) => r.map(escape).join(";"))];
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function emptyMetrics(): OperationalMetricsDto {
  return {
    horasTrabalhadas: 0,
    abastecimentosCount: 0,
    litrosAbastecidos: 0,
    consumoMedioGeral: 0,
    horasPorDia: [],
    consumoPorContrato: [],
    consumoPorMaquina: [],
  };
}

export function filterByMaquinaIds<T extends { maquinaId: string }>(items: T[], maquinaIds: string[]): T[] {
  if (maquinaIds.length === 0) return [];
  const set = new Set(maquinaIds);
  return items.filter((i) => set.has(i.maquinaId));
}

export function filterMaquinariosByIds(maquinarios: MaquinarioDto[], maquinaIds: string[]): MaquinarioDto[] {
  if (maquinaIds.length === 0) return [];
  const set = new Set(maquinaIds);
  return maquinarios.filter((m) => set.has(m.id));
}

/** Máquinas disponíveis no modal de abastecimento (contrato > garagem > contexto geral). */
export function resolveMaquinariosAbastecimentoModal(
  maquinasFrota: MaquinarioDto[],
  opts: {
    contrato?: Pick<ContratoDto, "maquinaIds"> | null;
    presetMaquinaId?: string | null;
    editingMaquinaId?: string | null;
    fallback: MaquinarioDto[];
  },
): MaquinarioDto[] {
  if (opts.contrato?.maquinaIds?.length) {
    const scoped = filterMaquinariosByIds(maquinasFrota, opts.contrato.maquinaIds);
    if (scoped.length > 0) return scoped;
  }
  if (opts.presetMaquinaId) {
    const m = maquinasFrota.find((x) => x.id === opts.presetMaquinaId);
    if (m) return [m];
  }
  if (opts.editingMaquinaId) {
    const m = maquinasFrota.find((x) => x.id === opts.editingMaquinaId);
    if (m) return [m];
  }
  return opts.fallback;
}

export function resolveMaquinaNome(
  maquinarios: MaquinarioDto[],
  maquinaId: string,
  fallback: string,
): string {
  return maquinarios.find((m) => m.id === maquinaId)?.nome ?? fallback;
}

export function formatContratoValor(
  contrato: Pick<
    ContratoDto,
    | "modeloContrato"
    | "valorContrato"
    | "valorHora"
    | "valorHoraPorMaquina"
    | "valorCarradaVigencias"
    | "maquinaIds"
    | "tipoContrato"
    | "valorMensal"
  >,
): string {
  if (isContratoEquipamento(contrato)) {
    if (isContratoModeloMensalEquipamento(contrato.modeloContrato)) {
      return (contrato.valorMensal ?? 0) > 0
        ? `R$ ${formatNumber(contrato.valorMensal ?? 0, 2)}/mês`
        : "—";
    }
    return contrato.valorContrato > 0 ? `R$ ${formatNumber(contrato.valorContrato, 2)}` : "—";
  }
  if (isContratoPorHoras(contrato.modeloContrato)) {
    const valores = contrato.maquinaIds
      .map((id) => contrato.valorHoraPorMaquina?.[id] ?? (contrato.valorHora > 0 ? contrato.valorHora : 0))
      .filter((v) => v > 0);
    if (valores.length === 0) return "—";
    const min = Math.min(...valores);
    const max = Math.max(...valores);
    if (min === max) return `R$ ${formatNumber(min, 2)}/h`;
    return `R$ ${formatNumber(min, 2)}–${formatNumber(max, 2)}/h`;
  }
  if (isContratoPorCarradas(contrato.modeloContrato)) {
    const list = contrato.valorCarradaVigencias ?? [];
    const aberto = [...list].reverse().find((v) => !v.vigenteAte && !v.dataFim && v.valorUnitario > 0);
    const atual =
      aberto ??
      [...list]
        .sort((a, b) => {
          const da = a.vigenteDesde || a.dataInicio || "";
          const db = b.vigenteDesde || b.dataInicio || "";
          return db.localeCompare(da);
        })
        .find((v) => v.valorUnitario > 0);
    if (!atual) return "—";
    const outros = list.filter((v) => v.valorUnitario > 0).length > 1;
    return outros
      ? `R$ ${formatNumber(atual.valorUnitario, 2)}/carrada (com histórico)`
      : `R$ ${formatNumber(atual.valorUnitario, 2)}/carrada`;
  }
  return contrato.valorContrato > 0 ? `R$ ${formatNumber(contrato.valorContrato, 2)}` : "—";
}

export function snapshotDataVersion(snapshot: LocacoesSnapshot | null): number {
  if (!snapshot) return 0;
  return (
    snapshot.maquinarios.length +
    (snapshot.contratos?.length ?? 0) +
    (snapshot.registrosHorimetro?.length ?? 0) +
    (snapshot.abastecimentos?.length ?? 0) +
    snapshot.locacoes.length +
    snapshot.manutencoes.length
  );
}
export function snapshotHasOperationalData(snapshot: LocacoesSnapshot | null): boolean {
  if (!snapshot) return false;
  return (snapshot.registrosHorimetro?.length ?? 0) > 0 || (snapshot.abastecimentos?.length ?? 0) > 0;
}

/** Máquina sem locação ativa (`status === "ativa"`). Exclui inativas do pátio operacional. */
export function hasLocacaoAtivaMaquina(maquinaId: string, locacoes: LocacaoDto[]): boolean {
  return locacoes.some((l) => l.maquinaId === maquinaId && l.status === "ativa");
}

export function filterMaquinasNaGaragem(
  maquinarios: MaquinarioDto[],
  locacoes: LocacaoDto[],
  contratos: ContratoDto[] = [],
  filtroTipo: FiltroTipoAtivo = "todos",
): MaquinarioDto[] {
  const ativosEmContrato = new Set(
    contratos
      .filter((c) => c.status === "ativo")
      .flatMap((c) => c.maquinaIds),
  );
  return filterMaquinariosByTipo(
    maquinarios.filter(
      (m) => !ativosEmContrato.has(m.id) && !hasLocacaoAtivaMaquina(m.id, locacoes),
    ),
    filtroTipo,
  );
}

export function filterMaquinariosByTipo(
  maquinarios: MaquinarioDto[],
  filtroTipo: FiltroTipoAtivo,
): MaquinarioDto[] {
  if (filtroTipo === "todos") return maquinarios;
  return maquinarios.filter((m) => resolveAtivoTipo(m) === filtroTipo);
}

export function filterContratosByTipo(
  contratos: ContratoDto[],
  _maquinarios: MaquinarioDto[],
  filtroTipo: FiltroTipoAtivo,
): ContratoDto[] {
  if (filtroTipo === "todos") return contratos;
  return contratos.filter(
    (c) => resolveAtivoTipo({ tipoAtivo: c.tipoContrato, tipo: "" }) === filtroTipo,
  );
}

export function inferTipoContratoFromMaquinas(
  maquinaIds: string[],
  maquinarios: MaquinarioDto[],
): AtivoTipo | null {
  if (maquinaIds.length === 0) return null;
  const tipos = new Set(maquinaIds.map((id) => resolveAtivoTipo(maquinarios.find((m) => m.id === id) ?? { tipoAtivo: "maquinario", tipo: "" })));
  if (tipos.size > 1) return null;
  return tipos.has("equipamento") ? "equipamento" : "maquinario";
}

export function getUltimaLocacaoEncerrada(maquinaId: string, locacoes: LocacaoDto[]): LocacaoDto | null {
  const encerradas = locacoes.filter(
    (l) => l.maquinaId === maquinaId && (l.status === "encerrada" || l.status === "cancelada"),
  );
  if (encerradas.length === 0) return null;
  return [...encerradas].sort((a, b) => {
    const da = a.dataFimReal ?? a.dataInicio;
    const db = b.dataFimReal ?? b.dataInicio;
    return db.localeCompare(da);
  })[0];
}

export function hasManutencaoAbertaMaquina(maquinaId: string, manutencoes: ManutencaoDto[]): boolean {
  return manutencoes.some(
    (m) =>
      m.maquinaId === maquinaId &&
      m.status !== "finalizada" &&
      m.statusAprovacao !== "rejeitado",
  );
}

export function filterManutencoesAbertasMaquina(maquinaId: string, manutencoes: ManutencaoDto[]): ManutencaoDto[] {
  return manutencoes.filter(
    (m) =>
      m.maquinaId === maquinaId &&
      m.status !== "finalizada" &&
      m.statusAprovacao !== "rejeitado",
  );
}

export function filterAbastecimentosMaquina(
  abastecimentos: AbastecimentoDto[],
  maquinaId: string,
): AbastecimentoDto[] {
  return [...abastecimentos]
    .filter((a) => a.maquinaId === maquinaId)
    .sort((a, b) => b.data.localeCompare(a.data) || b.createdAt.localeCompare(a.createdAt));
}

/** Classes compartilhadas — alinhadas ao padrão Financeiro/Suprimentos. */
export const locacoesPanelClass =
  "rounded-xl border border-slate-200 bg-white p-5 shadow-card";
export const locacoesTabContentClass = "flex flex-col gap-6";
export const locacoesTableWrapClass =
  "overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-card";
export const locacoesTableHeadClass =
  "bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500";
export const locacoesSubToolbarClass =
  "rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-card";
export const locacoesFilterChipClass =
  "inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 text-slate-700";
export const locacoesSectionTitleClass =
  "flex items-center gap-2 text-sm font-semibold tracking-tight text-slate-700";
export const locacoesListItemClass =
  "flex items-start justify-between gap-3 py-3.5 text-sm leading-relaxed first:pt-0 last:pb-0";
export const locacoesListMetaClass = "mt-1 text-xs leading-relaxed text-slate-500";
export const locacoesTabClass = (active: boolean) =>
  [
    "inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors",
    active
      ? "bg-brand-50 text-brand-700 ring-1 ring-brand-200"
      : "text-slate-500 hover:bg-slate-100 hover:text-slate-700",
  ].join(" ");
export const locacoesPillToggleClass = (active: boolean) =>
  [
    "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
    active ? "bg-brand-50 text-brand-700 ring-1 ring-brand-200" : "text-slate-600 hover:bg-slate-100 hover:text-slate-800",
  ].join(" ");

/** Garante que as fotos do relatório já estejam decodificadas antes do print. */
export async function waitForPrintableImages(rootSelector = ".locacoes-report-print"): Promise<void> {
  const root = document.querySelector(rootSelector);
  if (!root) return;

  const imgs = Array.from(root.querySelectorAll("img"));
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((resolve) => {
          const src = img.currentSrc || img.getAttribute("src") || "";
          if (!src) {
            resolve();
            return;
          }
          if (img.complete && img.naturalHeight > 0) {
            resolve();
            return;
          }
          let settled = false;
          const finish = () => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            resolve();
          };
          const timer = window.setTimeout(finish, 12000);
          img.addEventListener("load", finish, { once: true });
          img.addEventListener("error", finish, { once: true });
          const probe = new Image();
          probe.onload = finish;
          probe.onerror = finish;
          probe.src = src;
          void img.decode?.().then(finish).catch(() => undefined);
        }),
    ),
  );

  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}
